import React, { memo, useEffect, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';

import { firstCompanyIdSelector } from 'Containers/Projects/selectors';
import { paymentsSelector } from 'Containers/RocketPay/selectors';

import { getPaymentList, cancelPayment } from 'Containers/RocketPay/actions';
import { setToaster } from 'Containers/Core/actions';

import { usePaymentFunctions } from 'Context/RocketPay';
import { RocketPayPending } from 'Components/RocketPay';

type PaymentDateRange = {
  startDate: string;
  endDate: string;
};

export type PaymentFilters = {
  startsBetween?: PaymentDateRange;
  email?: string;
  payment_status?: string;
};

function RocketPayPendingContainer() {
  const dispatch = useDispatch();
  const firstCompanyId = useSelector(firstCompanyIdSelector, areEqual);
  const payments = useSelector(paymentsSelector, areEqual);

  const [sortBy, setSortBy] = useState('-created_at');
  const [pageCount, setPageCount] = useState(1);
  const [initialPage, setInitialPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [cancellingPaymentId, setCancellingPaymentId] = useState<number | null>(null);

  const { emailFilters, statusFilters, savedEmailFilters, savedStatusFilters, savedDateFilter }: any =
    usePaymentFunctions();

  // sort by: -field === DESC, field === ASC
  // prefix with '-' for DESC
  const onClickSort = useCallback(
    (sort: string) => {
      if (sortBy.includes('-') && sortBy === sort) {
        setSortBy(sort.replace('-', ''));
      } else {
        setSortBy(sort);
      }
      setInitialPage(0);
    },
    [sortBy]
  );

  // set meta data
  useEffect(() => {
    if (payments?.data?.length > 0) {
      const { meta } = payments;
      const { total } = meta;

      setPageCount(total <= 15 ? 1 : Number(total / 15));
    }
  }, [payments]);

  const onPageChange = useCallback(({ selected: page }: any) => {
    setInitialPage(page);
    getPayments();
  }, []);

  const getPaymentStatusFilter = (filter: string[]) => {
    const statuses = statusFilters.filter((status) => filter.includes(status.id));
    return statuses.map((status) => status.name).join(',');
  };

  const getEmailFilter = (filter: string[]) => {
    const emails = emailFilters.filter((email) => filter.includes(email.id));
    return emails.map((email) => email.name).join(',');
  };

  const getPayments = useCallback(async () => {
    const filters: PaymentFilters = {};
    if (savedDateFilter) {
      filters.startsBetween = savedDateFilter;
    }
    if (savedStatusFilters.length) {
      filters.payment_status = getPaymentStatusFilter(savedStatusFilters);
    }
    if (savedEmailFilters.length) {
      filters.email = getEmailFilter(savedEmailFilters);
    }
    setIsLoading(true);
    await dispatch(getPaymentList(firstCompanyId, initialPage + 1, sortBy, filters));
    setIsLoading(false);
  }, [firstCompanyId, sortBy, initialPage, savedEmailFilters, savedStatusFilters, savedDateFilter]);

  const onCancelPaymentClick = useCallback((paymentId: number) => {
    setCancellingPaymentId(paymentId);
  }, []);

  const onConfirmCancelPayment = useCallback(async () => {
    if (!cancellingPaymentId) return;
    const result = await dispatch(cancelPayment(firstCompanyId, cancellingPaymentId));
    if (result) {
      setCancellingPaymentId(null);
      getPayments();
    } else {
      dispatch(setToaster('Unable to cancel this payment request. It may have already been completed or cancelled.', false));
    }
  }, [cancellingPaymentId, firstCompanyId, getPayments]);

  const onDismissCancelModal = useCallback(() => {
    setCancellingPaymentId(null);
  }, []);

  // initial API call
  useEffect(() => {
    if (firstCompanyId) {
      getPayments();
    }
  }, [firstCompanyId, sortBy, initialPage, savedEmailFilters, savedStatusFilters, savedDateFilter]);

  return (
    <RocketPayPending
      payments={payments}
      sortBy={sortBy}
      onClickSort={onClickSort}
      pageCount={pageCount}
      initialPage={initialPage}
      onPageChange={onPageChange}
      isLoading={isLoading}
      cancellingPaymentId={cancellingPaymentId}
      onCancelPaymentClick={onCancelPaymentClick}
      onConfirmCancelPayment={onConfirmCancelPayment}
      onDismissCancelModal={onDismissCancelModal}
    />
  );
}

const RocketPayPendingContainerMemo = memo(RocketPayPendingContainer, areEqual);

export { RocketPayPendingContainerMemo as RocketPayPending };
